import type { EffectsDetail } from "daedalus-renderer-phaser";
import { CLOUD_SHADOW_PRESET, cloudCountFor, dryGround, stormFlashAt, turnOf, weatherAt, wetStateAt, wetAfterRain, wetAfterStop, weatherStrengthSource, windAt, type CloudField, type Wind, type WindHeading, type WindStrength, type WeatherKind, type WeatherTable, type WetState } from "odyssey-shared";
import type { EffectsChoice } from "../effects-setting.ts";

/**
 * Where the page's weather comes from: the shared deterministic `weatherAt` for the map's region and the clock, or a
 * developer's forced weather, and how the Effects setting and the render resolution cut the rain back.
 */

/**
 * A weather a development page forces with `?weather=`.
 */
export type ForcedWeather = "clear" | "rain" | "storm";

/**
 * Reads `?weather=`.
 *
 * @param raw The parameter's value, or null when the page has none.
 * @returns The forced weather, or null for anything but `clear`, `rain` or `storm`.
 */
export function parseForcedWeather(raw: string | null): ForcedWeather | null {
  return raw === "clear" || raw === "rain" || raw === "storm" ? raw : null;
}

/**
 * Reads `?wet=` and `?dry=` of a development page: the state the ground is in, held still, so a picture can be taken
 * at any point of the soak. `wet=<n>` is n real minutes of steady rain on dry ground; `dry=<n>` is n real minutes after rain
 * that had soaked the ground fully stopped.
 *
 * @param wet The `wet` parameter's value, or null.
 * @param dry The `dry` parameter's value, or null.
 * @returns The state, or null when neither is a number of minutes from 0 to 240.
 */
export function parseForcedWet(wet: string | null, dry: string | null): WetState | null {
  /**
   * Reads one parameter as minutes from 0 to 240.
   *
   * @param raw The parameter's value, or null.
   * @returns The minutes, or null.
   */
  const minutes = (raw: string | null): number | null => {
    const value = raw === null || raw === "" ? Number.NaN : Number(raw);
    return Number.isFinite(value) && value >= 0 && value <= 240 ? value : null;
  };
  const rained = minutes(wet);
  if (rained !== null) return wetAfterRain(rained);
  const dried = minutes(dry);
  return dried === null ? null : wetAfterStop(dried);
}

/**
 * A development force that may differ by map: the value of each named map, and the value every other map takes.
 */
export interface ForcedByMap<T> {
  readonly all: T | null;
  readonly maps: Readonly<Record<string, T>>;
}

/**
 * Reads a force that may name maps: entries apart by commas, each `<map>:<value>` or a bare value for every other map, so two
 * maps beside each other can be shown in different weather.
 *
 * @param raw The parameter's value, or null when the page has none.
 * @param read Reads one value, or gives null for one it does not know.
 * @returns The force, or null when the value is missing or any entry is unreadable.
 */
function parseByMap<T>(raw: string | null, read: (value: string) => T | null): ForcedByMap<T> | null {
  if (raw === null || raw === "") return null;
  let all: T | null = null;
  const maps: Record<string, T> = {};
  for (const entry of raw.split(",")) {
    const cut = entry.lastIndexOf(":");
    const value = read(cut < 0 ? entry : entry.slice(cut + 1));
    if (value === null) return null;
    if (cut < 0) all = value;
    else maps[entry.slice(0, cut)] = value;
  }
  return { all, maps };
}

/**
 * The value a force holds for one map.
 *
 * @param force The force, or null or undefined for none.
 * @param mapId The map's id.
 * @returns The map's own value, else the value for every map, else null.
 */
export function forcedFor<T>(force: ForcedByMap<T> | null | undefined, mapId: string): T | null {
  if (force === null || force === undefined) return null;
  return Object.hasOwn(force.maps, mapId) ? (force.maps[mapId] as T) : force.all;
}

/**
 * Reads `?weather=` by map (`main:clear,east:rain`, or `rain` for every map).
 *
 * @param raw The parameter's value, or null.
 * @returns The force, or null.
 */
export function parseForcedWeatherByMap(raw: string | null): ForcedByMap<ForcedWeather> | null {
  return parseByMap(raw, parseForcedWeather);
}

/**
 * Reads `?wet=` and `?dry=` by map, each as `parseForcedWet` reads one value; a map named in both takes its wet minutes.
 *
 * @param wet The `wet` parameter's value, or null.
 * @param dry The `dry` parameter's value, or null.
 * @returns The force, or null when neither reads.
 */
export function parseForcedWetByMap(wet: string | null, dry: string | null): ForcedByMap<WetState> | null {
  const rained = parseByMap(wet, (value) => parseForcedWet(value, null));
  const dried = parseByMap(dry, (value) => parseForcedWet(null, value));
  if (rained === null) return dried;
  if (dried === null) return rained;
  return { all: rained.all ?? dried.all, maps: { ...dried.maps, ...rained.maps } };
}

/**
 * The steady strength a forced weather holds (`weatherStrength`): 0 clear, 1 rain, 2 storm.
 *
 * @param forced The forced weather.
 * @returns Its strength.
 */
export function forcedStrength(forced: ForcedWeather): number {
  return forced === "clear" ? 0 : forced === "rain" ? 1 : 2;
}

/**
 * A wind a development page forces with `?wind=`: a strength, and a heading when the value names one.
 */
export interface ForcedWind {
  readonly strength: WindStrength;
  readonly heading: WindHeading | null;
}

/**
 * Reads `?wind=`.
 *
 * @param raw The parameter's value, or null when the page has none: `0` to `3`, optionally followed by `:left` or `:right`.
 * @returns The forced wind, or null for anything else.
 */
export function parseForcedWind(raw: string | null): ForcedWind | null {
  const match = raw === null ? null : /^([0-3])(?::(left|right))?$/.exec(raw);
  return match === null ? null : { strength: Number(match[1]) as WindStrength, heading: (match[2] as WindHeading | undefined) ?? null };
}

/**
 * Reads `?clouds=`.
 *
 * @param raw The parameter's value, or null when the page has none.
 * @returns `fair` or `cloudy`, or null for anything else.
 */
export function parseForcedClouds(raw: string | null): "fair" | "cloudy" | null {
  return raw === "fair" || raw === "cloudy" ? raw : null;
}

/**
 * The detail the effects run at: the viewer's choice, held at Reduced when the page renders below full resolution (Half or
 * Third, ADR-093 decision 10), since that setting is a machine asking for less work.
 *
 * @param choice The Effects choice.
 * @param divisor The render divisor in force (1 is Full).
 * @returns The detail.
 */
export function effectsDetail(choice: EffectsChoice, divisor: number): EffectsDetail {
  if (choice === "off") return "off";
  return choice === "reduced" || divisor > 1 ? "reduced" : "full";
}

/**
 * What the weather view reads each frame.
 */
export interface WeatherFeed {
  strength(epochMs: number): number;
  flash(epochMs: number, strength: number): number;
  wetStart(epochMs: number): WetState;
  readonly wetFrozen: boolean;
  wind(epochMs: number): Wind;
  clouds(epochMs: number, reduced: boolean): CloudField;
}

/**
 * What a feed is made from.
 */
export interface WeatherFeedInput {
  readonly table: WeatherTable;
  readonly worldSeed: number;
  readonly regionId: string;
  readonly forced: ForcedWeather | null;
  readonly forcedWet?: WetState | null;
  readonly forcedWind?: ForcedWind | null;
  readonly forcedClouds?: "fair" | "cloudy" | null;
}

/**
 * The feed of one region.
 *
 * @param input The table, the world seed, the region and the developer's force.
 * @returns A feed whose strength is the shared weather of the region, or the forced weather. The weather of a turn is worked out
 *   once, when the turn changes, so a frame allocates nothing.
 */
export function weatherFeed(input: WeatherFeedInput): WeatherFeed {
  const forced = input.forced === null ? null : forcedStrength(input.forced);
  const forcedWet = input.forcedWet ?? null;
  /**
   * The strength at a time: the forced weather's, or the region's.
   *
   * @param epochMs The time in milliseconds since the Unix epoch.
   * @returns The strength.
   */
  const shared = weatherStrengthSource(input.table, input.worldSeed, input.regionId);
  const strength = (epochMs: number): number => forced ?? shared(epochMs);
  const forcedWind = input.forcedWind ?? null;
  const forcedClouds = input.forcedClouds ?? null;
  let windTurn = -1;
  let baseClouds = 0;
  const wind: { strength: WindStrength; heading: WindHeading } = { strength: 0, heading: "right" };
  const field: { -readonly [K in keyof CloudField]: CloudField[K] } = { turn: 0, count: 0, speedPxPerS: 0, heading: "right" };
  const windOfTurn = (turn: number): void => {
    if (turn === windTurn) return;
    windTurn = turn;
    const natural = windAt(input.table, input.worldSeed, input.regionId, turn);
    const weather = weatherAt(input.table, input.worldSeed, input.regionId, turn);
    const kind: WeatherKind = input.forced ?? weather.kind;
    wind.strength = forcedWind?.strength ?? (input.forced === "storm" ? 3 : natural.strength);
    wind.heading = forcedWind?.heading ?? natural.heading;
    baseClouds = forcedClouds === null ? cloudCountFor(CLOUD_SHADOW_PRESET, kind, weather.wetness) : forcedClouds === "cloudy" ? CLOUD_SHADOW_PRESET.cloudyCount : CLOUD_SHADOW_PRESET.fairCount;
  };
  return {
    strength,
    wetStart: (epochMs) => (forcedWet !== null ? { ...forcedWet } : forced === null ? wetStateAt(strength, epochMs) : dryGround()),
    wetFrozen: forcedWet !== null,
    flash: (epochMs, strength) => stormFlashAt(input.worldSeed, input.regionId, epochMs, strength),
    wind: (epochMs) => {
      windOfTurn(turnOf(epochMs));
      return wind;
    },
    clouds: (epochMs, reduced) => {
      const turn = turnOf(epochMs);
      windOfTurn(turn);
      field.turn = turn;
      field.count = reduced ? Math.ceil(baseClouds / 2) : baseClouds;
      field.speedPxPerS = CLOUD_SHADOW_PRESET.speedPxPerS[wind.strength];
      field.heading = wind.heading;
      return field;
    },
  };
}
